import React, { useEffect, useState } from "react";
import { Loader2, Lock, Plus, Trash2 } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { APPROVER_ROLES } from "@/lib/stages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Panel, DataTable, Fld, money, inputCls } from "./shared";
import { AddAccountDialog } from "./AddAccountDialog";

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Manila" });
const nextDay = (d) => { const x = new Date(`${d}T00:00:00Z`); x.setUTCDate(x.getUTCDate() + 1); return x.toISOString().slice(0, 10); };

function StartDay({ acc, latest, onDone }) {
  const [date, setDate] = useState(latest ? (today() > latest.date ? today() : nextDay(latest.date)) : today());
  const [beginning, setBeginning] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await api.post("/finance/cash/days", { account_id: acc.id, date, beginning: latest ? null : parseFloat(beginning) });
      toast.success(`Day ${date} started`); onDone();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  return (
    <Panel title="Start a new day" subtitle={latest ? `Beginning balance carries over from the ${latest.date} closing.` : "First day for this account: enter the beginning balance."}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
        <Fld label="Date"><Input data-testid="input-cash-day-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} /></Fld>
        <Fld label="Beginning balance">
          {latest ? <div data-testid="cash-carried-beginning" className="font-mono-ops text-lg text-sky-300 py-1.5">{money(latest.closing)}</div>
            : <Input data-testid="input-cash-beginning" type="number" min="0" value={beginning} onChange={(e) => setBeginning(e.target.value)} placeholder="0.00" className={`font-mono-ops ${inputCls}`} />}
        </Fld>
        <Button data-testid="button-start-cash-day" onClick={go} disabled={busy || !date || (!latest && beginning === "")} className="bg-blue-600 hover:bg-blue-500">
          {busy && <Loader2 size={14} className="mr-2 animate-spin" />} Start day
        </Button>
      </div>
    </Panel>
  );
}

function EntryForm({ day, onSaved }) {
  const [f, setF] = useState({ type: "in", description: "", amount: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/finance/cash/days/${day.id}/entries`, { ...f, amount: parseFloat(f.amount) });
      setF({ type: f.type, description: "", amount: "" }); onSaved();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr_140px_auto] gap-2">
      <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
        <SelectTrigger data-testid="select-cash-entry-type" className={inputCls}><SelectValue /></SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
          <SelectItem value="in">Additional fund (in)</SelectItem>
          <SelectItem value="out">Expense / payout (out)</SelectItem>
        </SelectContent>
      </Select>
      <Input data-testid="input-cash-entry-desc" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Description" className={inputCls} />
      <Input data-testid="input-cash-entry-amount" type="number" min="0" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="0.00" className={`text-right font-mono-ops ${inputCls}`} />
      <Button data-testid="button-add-cash-entry" onClick={save} disabled={busy || !f.description.trim() || !(parseFloat(f.amount) > 0)} className="bg-blue-600 hover:bg-blue-500">
        <Plus size={14} className="mr-1" /> Add
      </Button>
    </div>
  );
}

const Line = ({ label, sub, amount, sign, testid, onRemove }) => (
  <div data-testid={testid} className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-800 text-sm">
    <div className="min-w-0"><div className="text-slate-200 truncate">{label}</div>{sub && <div className="text-[11px] text-slate-500">{sub}</div>}</div>
    <div className="flex items-center gap-2 shrink-0">
      <span className={`font-mono-ops ${sign > 0 ? "text-emerald-300" : "text-rose-300"}`}>{sign > 0 ? "+" : "−"}{money(amount)}</span>
      {onRemove && <button data-testid={`${testid}-remove`} onClick={onRemove} className="text-slate-500 hover:text-rose-300 transition-colors" aria-label="Remove"><Trash2 size={14} /></button>}
    </div>
  </div>
);

function DayCard({ day, canClose, onChanged }) {
  const open = day.status === "Open";
  const [busy, setBusy] = useState(false);
  const act = async (fn) => { setBusy(true); try { await fn(); onChanged(); } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); } };
  const close = () => window.confirm(`Close ${day.date} with a closing balance of ${money(day.closing)}? It becomes the next day's beginning balance and is locked.`)
    && act(() => api.post(`/finance/cash/days/${day.id}/close`).then(() => toast.success(`${day.date} closed`)));
  return (
    <Panel title={`${day.account_name} · ${day.date}`} subtitle={open ? "Open — Finance records entries; Finance Manager or Admin closes the day." : `Closed by ${day.closed?.by_name}`}
           right={open && canClose && <Button data-testid="button-close-cash-day" onClick={close} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500"><Lock size={14} className="mr-2" /> Close day</Button>}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {[["Beginning", day.beginning, "beginning", "text-slate-100"], ["Money in", day.total_in, "in", "text-emerald-300"],
          ["Money out", day.total_out, "out", "text-rose-300"], [open ? "Closing (so far)" : "Closing", day.closing, "closing", "text-sky-300"]].map(([l, v, k, c]) => (
          <div key={k} className="bg-slate-950/50 border border-slate-800 rounded-md p-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{l}</div>
            <div data-testid={`cash-day-${k}`} className={`font-mono-ops text-lg mt-1 ${c}`}>{money(v)}</div>
          </div>
        ))}
      </div>
      {open && <EntryForm day={day} onSaved={onChanged} />}
      <div className="mt-4">
        {day.entries.length === 0 && day.auto.length === 0 && <div className="text-sm text-slate-500">No movements yet.</div>}
        {day.entries.map((e, i) => (
          <Line key={e.id} testid={`cash-entry-${i}`} label={e.description} sub={`${e.type === "in" ? "Additional fund" : "Expense"} · ${e.by_name}`} amount={e.amount}
                sign={e.type === "in" ? 1 : -1} onRemove={open ? () => act(() => api.delete(`/finance/cash/days/${day.id}/entries/${e.id}`)) : null} />
        ))}
        {day.auto.map((a, i) => (
          <Line key={`a${i}`} testid={`cash-auto-${i}`} label={`${a.source} ${a.ref}`} sub={`${a.description} · ${a.date} · automatic`} amount={a.amount} sign={-1} />
        ))}
      </div>
    </Panel>
  );
}

export function AccountPicker({ accounts, value, onChange, onAdded, canManage }) {
  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid="select-cash-account" className={`sm:w-72 ${inputCls}`}><SelectValue placeholder="Choose account" /></SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
          {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.type === "cash" ? "Cash" : "Bank"})</SelectItem>)}
        </SelectContent>
      </Select>
      {canManage && <AddAccountDialog onAdded={onAdded} />}
    </div>
  );
}

export default function CashBookTab() {
  const { user } = useAuth();
  const canClose = APPROVER_ROLES.includes(user?.role);
  const [accounts, setAccounts] = useState([]);
  const [accId, setAccId] = useState("");
  const [days, setDays] = useState(null);
  const loadAccounts = () => api.get("/finance/cash/accounts").then((r) => { setAccounts(r.data); setAccId((v) => v || r.data[0]?.id || ""); })
    .catch((e) => toast.error(formatError(e)));
  const loadDays = () => accId && api.get("/finance/cash/days", { params: { account_id: accId } }).then((r) => setDays(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { loadAccounts(); }, []);
  useEffect(() => { setDays(null); loadDays(); /* eslint-disable-next-line */ }, [accId]);
  const acc = accounts.find((a) => a.id === accId);
  const latest = days?.[0];
  const columns = [
    { key: "date", label: "Date", mono: true }, { key: "beginning", label: "Beginning", right: true, render: (r) => money(r.beginning) },
    { key: "total_in", label: "In", right: true, render: (r) => <span className="text-emerald-300">{money(r.total_in)}</span> },
    { key: "total_out", label: "Out", right: true, render: (r) => <span className="text-rose-300">{money(r.total_out)}</span> },
    { key: "closing", label: "Closing", right: true, render: (r) => money(r.closing) },
    { key: "status", label: "Status", render: (r) => r.status }, { key: "closed", label: "Closed by", render: (r) => r.closed?.by_name || "—" },
  ];
  return (
    <div className="space-y-6">
      <AccountPicker accounts={accounts} value={accId} onChange={setAccId} canManage={canClose} onAdded={(a) => { loadAccounts(); setAccId(a.id); }} />
      {acc?.type === "bank" && <p className="text-xs text-slate-500">Approved check vouchers drawn from "{acc.name}" are deducted on the day they are approved; released post-dated checks on the release day.</p>}
      {acc?.auto_cash && <p className="text-xs text-slate-500">Approved operational funds and cash vouchers are deducted automatically on the day they are approved; released post-dated checks on the release day.</p>}
      {days === null ? <div className="text-sm text-slate-500">Loading…</div> : (
        <>
          {latest?.status === "Open" ? <DayCard day={latest} canClose={canClose} onChanged={() => { loadDays(); loadAccounts(); }} />
            : <StartDay key={`${accId}-${latest?.id}`} acc={acc} latest={latest} onDone={loadDays} />}
          <Panel title="Daily history" subtitle="Each closing balance becomes the next day's beginning balance.">
            <DataTable testid="cash-days-table" columns={columns} rows={days} empty="No days recorded yet." />
          </Panel>
        </>
      )}
    </div>
  );
}
